import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Field, PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Building2, Plus, Pencil, Trash2, Search } from "lucide-react";
import { LEN } from "@/lib/limites";

const I = String.fromCharCode(0xed);
const O = String.fromCharCode(0xf3);
const A = String.fromCharCode(0xe1);
const U = String.fromCharCode(0xfa);
const N = String.fromCharCode(0xf1);
const DASH = "\u2014";

const EMPTY = {
  codigo: "",
  nombre: "",
  sage_conectado: false,
  sage_api_url: "",
  sage_codigo_empresa: "",
  sage_codigo_catalogo: "",
  limite_documentos: "",
  limite_usuarios: "",
  multiempresa: false,
  multiempresa_codigo: "",
  sage_transportistas_proveedores: false,
  sage_proveedores_actividad: "",
  naturaleza_por_defecto: "",
  modo_agrupacion: "unico",
  demo: false,
  deca_proveedores: false,
  albaran_varios_decas: false,
  naturaleza_linea_albaran: false,
  sage_conductores: false,
  peso_transporte_sage: false,
  bultos_defecto_activo: false,
  bultos_defecto: "",
  cargador_es_cliente: false,
};

function normCodigo(v) {
  return String(v || "").trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "");
}

function fmtLimite(lim, usados, anio) {
  if (lim === null || lim === undefined || lim === "") return "Sin l" + I + "mite";
  const base = usados === null || usados === undefined ? String(lim) : usados + " / " + lim;
  return anio ? base + " (" + anio + ")" : base;
}

function TablaEmpresas({ titulo, items, vacio, onEdit, onRemove }) {
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
        <div>
          <div className="text-sm font-semibold text-slate-900">{titulo}</div>
          <div className="text-xs text-slate-500">{items.length} empresa{items.length === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="overflow-x-auto">
          <table className="deca-table w-full min-w-[1100px]">
          <thead>
            <tr>
              <th>{"C" + O + "digo"}</th>
              <th>Nombre</th>
              <th>Sage</th>
              <th>{"C" + O + "d. Sage"}</th>
              <th>Grupo</th>
              <th>Agrupaci{O}n</th>
              <th>DeCA proveedor</th>
              <th>Reutilizar alb.</th>
              <th>Nat. l{I}neas</th>
              <th>Conductores</th>
              <th>Peso transp.</th>
              <th>Bultos def.</th>
              <th>Carg. cliente</th>
              <th>{"L" + I + "mite DeCA / a" + N + "o"}</th>
              <th>{"L" + I + "mite usuarios"}</th>
              <th>URL API</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} data-testid={`empresa-row-${e.id}`}>
                <td className="font-mono text-xs font-semibold">{e.codigo || DASH}</td>
                <td className="font-medium">{e.nombre || DASH}</td>
                <td>{e.sage_conectado ? "S" + I : "No"}</td>
                <td className="font-mono text-xs">{e.sage_conectado ? (e.sage_codigo_empresa || DASH) : DASH}</td>
                <td className="font-mono text-xs">
                  {e.multiempresa && e.multiempresa_codigo ? (
                    <div>
                      <div className="font-semibold text-slate-800">{e.multiempresa_codigo}</div>
                      {(e.iguales || []).filter((x) => x.codigo !== e.codigo).length ? (
                        <div className="text-[10px] text-slate-500 font-sans mt-0.5 max-w-[160px] truncate">
                          {(e.iguales || []).filter((x) => x.codigo !== e.codigo).map((x) => x.codigo).join(", ")}
                        </div>
                      ) : null}
                    </div>
                  ) : DASH}
                </td>
                <td className="text-xs">{e.modo_agrupacion === "agrupado" ? "Por destino" : "Un solo DeCA"}</td>
                <td className="text-xs">{e.deca_proveedores ? "S" + I : "No"}</td>
                <td className="text-xs">{e.albaran_varios_decas ? "S" + I : "No"}</td>
                <td className="text-xs">{e.naturaleza_linea_albaran ? "S" + I : "No"}</td>
                <td className="text-xs">{e.sage_conductores ? "S" + I : "No"}</td>
                <td className="text-xs">{e.peso_transporte_sage ? "S" + I : "No"}</td>
                <td className="text-xs">{e.bultos_defecto_activo && e.bultos_defecto > 0 ? e.bultos_defecto : "No"}</td>
                <td className="text-xs">{e.cargador_es_cliente ? "S" + I : "No"}</td>
                <td className="font-mono text-xs">{fmtLimite(e.limite_documentos, e.documentos_usados, e.documentos_anio)}</td>
                <td className="font-mono text-xs">{fmtLimite(e.limite_usuarios, e.usuarios_usados)}</td>
                <td className="font-mono text-xs max-w-[220px] truncate">{e.sage_conectado ? (e.sage_api_url || DASH) : DASH}</td>
                <td className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-2" onClick={() => onEdit(e)} aria-label="Editar empresa" data-testid={`btn-edit-emp-${e.id}`}>
                    <Pencil className="h-3.5 w-3.5" aria-hidden="true"/>
                  </Button>
                  <Button size="sm" variant="outline" className="text-red-700" onClick={() => onRemove(e)} aria-label="Eliminar empresa" data-testid={`btn-del-emp-${e.id}`}>
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true"/>
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-12 text-center text-slate-500">
                  <Building2 className="h-8 w-8 mx-auto text-slate-400 mb-2"/>
                  {vacio}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function EmpresasMantPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/v1/empresas");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const filtrados = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((e) =>
      [e.codigo, e.nombre, e.sage_codigo_empresa, e.sage_api_url, e.multiempresa_codigo].join(" ").toLowerCase().includes(s)
    );
  }, [items, q]);
  const produccion = useMemo(() => filtrados.filter((e) => !e.demo), [filtrados]);
  const demos = useMemo(() => filtrados.filter((e) => e.demo), [filtrados]);

  const igualesHint = useMemo(() => {
    const g = normCodigo(form.multiempresa_codigo);
    if (!form.multiempresa || !g) return [];
    const yo = edit ? String(edit.codigo || "").toUpperCase() : normCodigo(form.codigo);
    return items
      .filter((e) => e.multiempresa && String(e.multiempresa_codigo || "").toUpperCase() === g && String(e.codigo || "").toUpperCase() !== yo)
      .map((e) => e.codigo);
  }, [items, form.multiempresa, form.multiempresa_codigo, form.codigo, edit]);

  const openNew = () => {
    setEdit(null);
    setForm({ ...EMPTY });
    setOpen(true);
  };

  const openEdit = (e) => {
    setEdit(e);
    setForm({
      codigo: e.codigo || "",
      nombre: e.nombre || "",
      sage_conectado: !!e.sage_conectado,
      sage_api_url: e.sage_api_url || "",
      sage_codigo_empresa: e.sage_codigo_empresa || "",
      sage_codigo_catalogo: e.sage_codigo_catalogo || "",
      limite_documentos: e.limite_documentos === null || e.limite_documentos === undefined ? "" : String(e.limite_documentos),
      limite_usuarios: e.limite_usuarios === null || e.limite_usuarios === undefined ? "" : String(e.limite_usuarios),
      multiempresa: !!e.multiempresa,
      multiempresa_codigo: e.multiempresa_codigo || "",
      sage_transportistas_proveedores: !!e.sage_transportistas_proveedores || String(e.codigo || "").toUpperCase() === "COTYMSE",
      sage_proveedores_actividad: e.sage_proveedores_actividad || "",
      naturaleza_por_defecto: e.naturaleza_por_defecto || "",
      modo_agrupacion: e.modo_agrupacion === "agrupado" ? "agrupado" : "unico",
      demo: !!e.demo,
      deca_proveedores: !!e.deca_proveedores,
      albaran_varios_decas: !!e.albaran_varios_decas,
      naturaleza_linea_albaran: !!e.naturaleza_linea_albaran,
      sage_conductores: !!e.sage_conductores,
      peso_transporte_sage: !!e.peso_transporte_sage,
      bultos_defecto_activo: !!e.bultos_defecto_activo,
      bultos_defecto: e.bultos_defecto ? String(e.bultos_defecto) : "",
      cargador_es_cliente: !!e.cargador_es_cliente,
    });
    setOpen(true);
  };

  const save = async () => {
    const codigo = normCodigo(form.codigo);
    if (!codigo) {
      toast.error("El c" + O + "digo de empresa es obligatorio");
      return;
    }
    if (form.sage_conectado && !form.sage_api_url.trim()) {
      toast.error("Indica la URL de la API de Sage");
      return;
    }
    if (form.sage_conectado && !String(form.sage_codigo_empresa || "").trim()) {
      toast.error("Indica el c" + O + "digo de empresa de Sage");
      return;
    }
    if (form.multiempresa && !normCodigo(form.multiempresa_codigo)) {
      toast.error("Indica el c" + O + "digo de grupo multiempresa");
      return;
    }
    let bultosDef = 0;
    if (form.bultos_defecto_activo) {
      bultosDef = parseInt(String(form.bultos_defecto || "").trim(), 10);
      if (!Number.isFinite(bultosDef) || bultosDef <= 0) {
        toast.error("Indica el n" + U + "mero de bultos por defecto");
        return;
      }
    } else if (String(form.bultos_defecto || "").trim() !== "") {
      bultosDef = parseInt(String(form.bultos_defecto).trim(), 10);
      if (!Number.isFinite(bultosDef) || bultosDef < 0) {
        toast.error("El n" + U + "mero de bultos por defecto no es v" + A + "lido");
        return;
      }
    }
    if (form.sage_conectado) {
      const url = form.sage_api_url.trim();
      if (!/^https?:\/\//i.test(url)) {
        toast.error("La URL de Sage debe empezar por http:// o https://");
        return;
      }
    }
    let limite = null;
    if (String(form.limite_documentos || "").trim() !== "") {
      limite = parseInt(String(form.limite_documentos).trim(), 10);
      if (!Number.isFinite(limite) || limite < 0) {
        toast.error("El l" + I + "mite de documentos debe ser un n" + U + "mero mayor o igual que 0");
        return;
      }
    }
    let limiteUsuarios = null;
    if (String(form.limite_usuarios || "").trim() !== "") {
      limiteUsuarios = parseInt(String(form.limite_usuarios).trim(), 10);
      if (!Number.isFinite(limiteUsuarios) || limiteUsuarios < 0) {
        toast.error("El l" + I + "mite de usuarios debe ser un n" + U + "mero mayor o igual que 0");
        return;
      }
    }
    setBusy(true);
    try {
      const payload = {
        nombre: form.nombre.trim(),
        sage_conectado: !!form.sage_conectado,
        sage_api_url: form.sage_conectado ? form.sage_api_url.trim() : "",
        sage_codigo_empresa: form.sage_conectado ? String(form.sage_codigo_empresa || "").trim() : "",
        sage_codigo_catalogo: form.sage_conectado ? String(form.sage_codigo_catalogo || "").trim() : "",
        limite_documentos: limite,
        limite_usuarios: limiteUsuarios,
        multiempresa: !!form.multiempresa,
        multiempresa_codigo: form.multiempresa ? normCodigo(form.multiempresa_codigo) : "",
        sage_transportistas_proveedores: !!form.sage_conectado && !!form.sage_transportistas_proveedores,
        sage_proveedores_actividad: !!form.sage_conectado && !!form.sage_transportistas_proveedores
          ? (form.sage_proveedores_actividad || "").trim()
          : "",
        naturaleza_por_defecto: (form.naturaleza_por_defecto || "").trim(),
        modo_agrupacion: form.modo_agrupacion === "agrupado" ? "agrupado" : "unico",
        demo: !!form.demo,
        deca_proveedores: !!form.deca_proveedores,
        albaran_varios_decas: !!form.albaran_varios_decas,
        naturaleza_linea_albaran: !!form.naturaleza_linea_albaran,
        sage_conductores: !!form.sage_conductores,
        peso_transporte_sage: !!form.peso_transporte_sage,
        bultos_defecto_activo: !!form.bultos_defecto_activo,
        bultos_defecto: form.bultos_defecto_activo ? bultosDef : (bultosDef || 0),
        cargador_es_cliente: !!form.cargador_es_cliente,
      };
      if (edit) await api.patch(`/api/v1/empresas/${edit.id}`, payload);
      else {
        payload.codigo = codigo;
        await api.post("/api/v1/empresas", payload);
      }
      setOpen(false);
      await load();
      toast.success(edit ? "Empresa actualizada" : "Empresa creada");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (e) => {
    const ok = window.confirm("Eliminar la empresa " + (e.nombre || e.codigo) + "?");
    if (!ok) return;
    try {
      await api.delete(`/api/v1/empresas/${e.id}`);
      await load();
      toast.success("Empresa eliminada");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Empresas"
        subtitle={"C" + O + "digo interno, l" + I + "mites, Sage y grupo multiempresa. Luego asignas ese c" + O + "digo a los usuarios."}
        testid="page-empresas"
        actions={
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid="btn-empresa-nueva">
            <Plus className="h-4 w-4 mr-2"/> Nueva empresa
          </Button>
        }
      />

      <Card className="p-4 mb-4">
        <div className="relative max-w-md">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-slate-400"/>
          <Input
            className="pl-8"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={"Buscar c" + O + "digo, nombre o Sage"}
            aria-label="Buscar empresas"
            data-testid="empresas-buscar"
          />
        </div>
      </Card>

      <div className="space-y-4">
        <TablaEmpresas
          titulo={"Producci" + O + "n"}
          items={produccion}
          vacio={items.length === 0 ? "No hay empresas de producci" + O + "n. Crea la primera." : "No hay empresas de producci" + O + "n con esa b" + U + "squeda."}
          onEdit={openEdit}
          onRemove={remove}
        />
        <TablaEmpresas
          titulo="Demo"
          items={demos}
          vacio={items.length === 0 ? "No hay empresas demo." : "No hay empresas demo con esa b" + U + "squeda."}
          onEdit={openEdit}
          onRemove={remove}
        />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[90vh] w-[calc(100vw-2rem)] max-w-2xl flex-col overflow-hidden gap-4">
          <DialogHeader className="shrink-0">
            <DialogTitle>{edit ? "Editar empresa" : "Nueva empresa"}</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1 space-y-3">
            <Field label={"C" + O + "digo interno *"}>
              <Input
                className="mt-1 font-mono uppercase"
                maxLength={LEN.codigoEmpresa}
                value={form.codigo}
                onChange={(e) => {
                  const c = normCodigo(e.target.value);
                  setForm({
                    ...form,
                    codigo: c,
                    sage_transportistas_proveedores: c === "COTYMSE" ? true : form.sage_transportistas_proveedores,
                  });
                }}
                placeholder="DEMO2"
                disabled={!!edit}
                data-testid="emp-codigo"
              />
            </Field>
            <Field label="Nombre">
              <Input className="mt-1" maxLength={LEN.razonSocial} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Transportes Peninsulares" data-testid="emp-nombre"/>
            </Field>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Empresa demo</div>
                <div className="text-xs text-slate-500">
                  Si es s{I}, se lista abajo, separada de las de producci{O}n.
                </div>
              </div>
              <Switch
                checked={!!form.demo}
                onCheckedChange={(v) => setForm({ ...form, demo: v })}
                aria-label="Empresa demo"
                data-testid="emp-demo"
              />
            </div>
            <Field label={"Naturaleza por defecto"}>
              <Input
                className="mt-1"
                maxLength={LEN.naturaleza}
                value={form.naturaleza_por_defecto}
                onChange={(e) => setForm({ ...form, naturaleza_por_defecto: e.target.value })}
                placeholder="Puertas y Molduras"
                data-testid="emp-naturaleza"
              />
            </Field>
            <p className="text-xs text-slate-500 -mt-1">
              Sale en el DeCA manual y en albaranes Sage si no se usa la descripci{O}n de la l{I}nea. Vac{I}o = Mercanc{I}a general.
            </p>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Naturaleza desde l{I}neas del albar{A}n</div>
                <div className="text-xs text-slate-500">
                  En DeCA de albaranes Sage, cada env{I}o usa la DescripcionArticulo del primer producto. Si esa l{I}nea no tiene texto, se usa el campo de arriba (o Mercanc{I}a general). El DeCA manual no cambia.
                </div>
              </div>
              <Switch
                checked={!!form.naturaleza_linea_albaran}
                onCheckedChange={(v) => setForm({ ...form, naturaleza_linea_albaran: v })}
                aria-label={"Naturaleza desde l" + I + "neas del albar" + A + "n"}
                data-testid="emp-naturaleza-lineas"
              />
            </div>
            <div>
              <div className="label-tag">Agrupaci{O}n DeCA por defecto</div>
              <Tabs value={form.modo_agrupacion || "unico"} onValueChange={(v) => setForm({ ...form, modo_agrupacion: v })}>
                <TabsList className="mt-1" data-testid="emp-modo-agrupacion">
                  <TabsTrigger value="unico">Un solo DeCA</TabsTrigger>
                  <TabsTrigger value="agrupado">Por destino</TabsTrigger>
                </TabsList>
              </Tabs>
              <p className="text-xs text-slate-500 mt-1">
                Sale al seleccionar albaranes y en el DeCA manual. Se puede cambiar en cada documento.
              </p>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">DeCA a proveedores</div>
                <div className="text-xs text-slate-500">
                  Permite emitir un DeCA con origen en el proveedor y destino en el cargador de la empresa. Activa el mantenimiento de proveedores.
                </div>
              </div>
              <Switch
                checked={!!form.deca_proveedores}
                onCheckedChange={(v) => setForm({ ...form, deca_proveedores: v })}
                aria-label="DeCA a proveedores"
                data-testid="emp-deca-proveedores"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Mantenimiento de conductores</div>
                <div className="text-xs text-slate-500">
                  Activa el cat{A}logo de conductores. Si la empresa tiene Sage, se cargan con GET /conductores (nombre, DNI y matr{I}culas).
                </div>
              </div>
              <Switch
                checked={!!form.sage_conductores}
                onCheckedChange={(v) => setForm({ ...form, sage_conductores: v })}
                aria-label="Mantenimiento de conductores"
                data-testid="emp-sage-conductores"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Peso desde transporte Sage</div>
                <div className="text-xs text-slate-500">
                  En albaranes Sage el peso del DeCA es Peso Bruto Transporte menos Tara Transporte (VPesoBrutoTransporte - VTaraTransporte). Si no hay tara, se usa el bruto de transporte. El resto de empresas sigue con el peso de cabecera o l{I}neas.
                </div>
              </div>
              <Switch
                checked={!!form.peso_transporte_sage}
                onCheckedChange={(v) => setForm({ ...form, peso_transporte_sage: v })}
                aria-label="Peso desde transporte Sage"
                data-testid="emp-peso-transporte"
              />
            </div>
            <div className="rounded-md border border-slate-200 px-3 py-2 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-sm font-medium text-slate-900">Bultos por defecto</div>
                  <div className="text-xs text-slate-500">
                    Si el albar{A}n no trae bultos (Sage, preview, API o DeCA manual), se usa este n{U}mero. Si Sage ya informa bultos, se respetan.
                  </div>
                </div>
                <Switch
                  checked={!!form.bultos_defecto_activo}
                  onCheckedChange={(v) => setForm({ ...form, bultos_defecto_activo: v })}
                  aria-label="Bultos por defecto"
                  data-testid="emp-bultos-defecto-activo"
                />
              </div>
              {form.bultos_defecto_activo ? (
                <Field label={"N" + U + "mero de bultos"}>
                  <Input
                    className="mt-1 font-mono"
                    type="number"
                    min={1}
                    max={99999}
                    step={1}
                    value={form.bultos_defecto}
                    onChange={(e) => setForm({ ...form, bultos_defecto: e.target.value })}
                    placeholder="1"
                    data-testid="emp-bultos-defecto"
                  />
                </Field>
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Elegir cargador contractual</div>
                <div className="text-xs text-slate-500">
                  Permite elegir en cada DeCA si el cargador contractual es la empresa o el cliente. El origen de carga sigue siendo el de la empresa. No aplica a DeCA a proveedor.
                </div>
              </div>
              <Switch
                checked={!!form.cargador_es_cliente}
                onCheckedChange={(v) => setForm({ ...form, cargador_es_cliente: v })}
                aria-label="Cargador contractual igual al cliente"
                data-testid="emp-cargador-es-cliente"
              />
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">{"Reutilizar albar" + A + "n en varios DeCA"}</div>
                <div className="text-xs text-slate-500">
                  Permite seleccionar un albar{A}n que ya est{A} en otro DeCA. Al elegirlo se avisa, pero no se bloquea.
                </div>
              </div>
              <Switch
                checked={!!form.albaran_varios_decas}
                onCheckedChange={(v) => setForm({ ...form, albaran_varios_decas: v })}
                aria-label={"Reutilizar albar" + A + "n en varios DeCA"}
                data-testid="emp-albaran-varios"
              />
            </div>
            <div>
              <Field label={"L" + I + "mite de documentos al a" + N + "o (vac" + I + "o = sin l" + I + "mite)"}>
                <Input
                  className="mt-1 font-mono"
                  type="number"
                  min={0}
                  max={1000000}
                  step={1}
                  maxLength={LEN.limiteDocumentos}
                  value={form.limite_documentos}
                  onChange={(e) => setForm({ ...form, limite_documentos: e.target.value })}
                  placeholder="50"
                  data-testid="emp-limite-documentos"
                />
              </Field>
              <p className="text-xs text-slate-500 mt-1">
                Se reinicia el 1 de enero. El recuento es del a{N}o en curso.
              </p>
            </div>
            <div>
              <Field label={"L" + I + "mite de usuarios (vac" + I + "o = sin l" + I + "mite)"}>
                <Input
                  className="mt-1 font-mono"
                  type="number"
                  min={0}
                  max={10000}
                  step={1}
                  maxLength={LEN.limiteUsuarios}
                  value={form.limite_usuarios}
                  onChange={(e) => setForm({ ...form, limite_usuarios: e.target.value })}
                  placeholder="5"
                  data-testid="emp-limite-usuarios"
                />
              </Field>
            </div>
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Usa Sage</div>
                <div className="text-xs text-slate-500">Si es s{I}, URL de la API y c{O}digo empresa Sage.</div>
              </div>
              <Switch
                checked={!!form.sage_conectado}
                onCheckedChange={(v) => setForm({ ...form, sage_conectado: v })}
                aria-label="Usa Sage"
                data-testid="emp-sage"
              />
            </div>
            {form.sage_conectado ? (
              <>
                <Field label={"C" + O + "digo empresa Sage *"}>
                  <Input
                    className="mt-1 font-mono"
                    maxLength={LEN.codigoEmpresa}
                    value={form.sage_codigo_empresa}
                    onChange={(e) => setForm({ ...form, sage_codigo_empresa: e.target.value.trim() })}
                    placeholder="20"
                    data-testid="emp-sage-codigo"
                  />
                </Field>
                <Field label={"C" + O + "d. Sage cat" + A + "logo (clientes / transportistas)"}>
                  <Input
                    className="mt-1 font-mono"
                    maxLength={LEN.codigoEmpresa}
                    value={form.sage_codigo_catalogo}
                    onChange={(e) => setForm({ ...form, sage_codigo_catalogo: e.target.value.trim() })}
                    placeholder="10"
                    data-testid="emp-sage-catalogo"
                  />
                </Field>
                <p className="text-xs text-slate-500 -mt-1">
                  Si el maestro de clientes est{A} en otra empresa Sage (p. ej. 10), p{O}nlo aqu{I}. Vac{I}o = el mismo c{O}digo de albaranes. En un grupo multiempresa, si no hay datos se prueba el resto autom{A}ticamente.
                </p>
                <Field label={"URL de la API Sage *"}>
                  <Input
                    className="mt-1"
                    maxLength={LEN.sageUrl}
                    value={form.sage_api_url}
                    onChange={(e) => setForm({ ...form, sage_api_url: e.target.value })}
                    placeholder="https://deca.apiventurasage.com"
                    data-testid="emp-sage-url"
                  />
                </Field>
                <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                  <div>
                    <div className="text-sm font-medium text-slate-900">Transportistas desde proveedores</div>
                    <div className="text-xs text-slate-500">
                      COTYMSE: el maestro es la tabla de proveedores Sage, no transportistas. El resto de empresas no lo usan.
                    </div>
                  </div>
                  <Switch
                    checked={!!form.sage_transportistas_proveedores}
                    onCheckedChange={(v) => setForm({ ...form, sage_transportistas_proveedores: v })}
                    aria-label="Transportistas desde proveedores"
                    data-testid="emp-sage-proveedores"
                  />
                </div>
                {form.sage_transportistas_proveedores ? (
                  <>
                    <Field label="Filtro Actividad">
                      <Input
                        className="mt-1"
                        maxLength={LEN.actividad}
                        value={form.sage_proveedores_actividad}
                        onChange={(e) => setForm({ ...form, sage_proveedores_actividad: e.target.value })}
                        placeholder="Ej. TRANSPORTE"
                        data-testid="emp-sage-actividad"
                      />
                    </Field>
                    <p className="text-xs text-slate-500 -mt-1">
                      Vac{I}o = todos los proveedores. Si lo rellenas, solo salen los que en Sage tienen ese valor en Actividad.
                    </p>
                  </>
                ) : null}
              </>
            ) : null}
            <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <div className="text-sm font-medium text-slate-900">Multiempresa</div>
                <div className="text-xs text-slate-500">
                  Si es s{I}, el usuario podr{A} cambiar entre las empresas con el mismo c{O}digo de grupo.
                </div>
              </div>
              <Switch
                checked={!!form.multiempresa}
                onCheckedChange={(v) => setForm({ ...form, multiempresa: v })}
                aria-label="Multiempresa"
                data-testid="emp-multiempresa"
              />
            </div>
            {form.multiempresa ? (
              <div>
                <Field label={"C" + O + "digo de grupo *"}>
                  <Input
                    className="mt-1 font-mono uppercase"
                    maxLength={LEN.codigoEmpresa}
                    value={form.multiempresa_codigo}
                    onChange={(e) => setForm({ ...form, multiempresa_codigo: normCodigo(e.target.value) })}
                    placeholder="GRUPO1"
                    data-testid="emp-multiempresa-codigo"
                  />
                </Field>
                {igualesHint.length ? (
                  <div className="text-[11px] text-slate-500 mt-1" data-testid="emp-multiempresa-iguales">
                    Empresas con este c{O}digo: {igualesHint.join(", ")}
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 mt-1">
                    Las empresas creadas con este mismo c{O}digo aparecer{A}n en el selector del usuario.
                  </div>
                )}
              </div>
            ) : null}
          </div>
          <DialogFooter className="shrink-0 border-t pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={save} disabled={busy} data-testid="emp-guardar">
              {busy ? "Guardando" + String.fromCharCode(0x2026) : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
